<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog documents-download-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="documents-download-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="documents-download-modal-title">{{ labels.title }}</h3>
          <p>{{ labels.contextMessage }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="labels.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content documents-download-modal__content">
        <div class="documents-download-modal__body">
          <section class="app-modal__section">
            <p v-if="resolvedScopeLabel !== ''" class="documents-download-modal__context-line">
              {{ resolvedScopeLabel }}
            </p>

            <p v-if="totalInvoices > 0" class="documents-download-modal__context-count">
              {{ labels.resultsFound(totalInvoices) }}
            </p>
            <div v-else class="documents-download-modal__info-box documents-download-modal__info-box--warning" role="status">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <p>{{ labels.noResultsMessage }}</p>
            </div>

            <div
              v-if="showDocumentTypeOptions"
              class="documents-download-modal__types"
              role="radiogroup"
              :aria-label="labels.documentTypeLabel"
            >
              <label
                v-for="option in resolvedDocumentTypeOptions"
                :key="option"
                class="app-modal-toggle documents-download-modal__type-item"
                :class="{ 'is-active': selectedDocumentType === option }"
              >
                <input v-model="selectedDocumentType" type="radio" :value="option">
                <span>{{ resolveDocumentTypeLabel(option) }}</span>
              </label>
            </div>

            <div v-if="showGroupingOptions" class="documents-download-modal__grouping-section">
              <p class="documents-download-modal__grouping-title">{{ labels.groupByLabel }}</p>
              <div class="documents-download-modal__grouping-options">
                <label
                  class="app-modal-toggle documents-download-modal__grouping-option"
                  :class="{ 'is-active': selectedGroupBy === 'customer' }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedGroupBy === 'customer'"
                    @change="setGroupBy('customer')"
                  >
                  <span>{{ labels.groupByOptions.customer }}</span>
                </label>
                <label
                  class="app-modal-toggle documents-download-modal__grouping-option"
                  :class="{ 'is-active': selectedGroupBy === 'month' }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedGroupBy === 'month'"
                    @change="setGroupBy('month')"
                  >
                  <span>{{ labels.groupByOptions.month }}</span>
                </label>
                <label
                  class="app-modal-toggle documents-download-modal__grouping-option"
                  :class="{ 'is-active': selectedGroupBy === 'none' }"
                >
                  <input
                    type="checkbox"
                    :checked="selectedGroupBy === 'none'"
                    @change="setGroupBy('none')"
                  >
                  <span>{{ labels.groupByOptions.none }}</span>
                </label>
              </div>
            </div>
          </section>

          <section v-if="latestRequest" class="app-modal__section documents-download-modal__last-download">
            <div class="app-modal__section-head documents-download-modal__section-head">
              <h4>{{ labels.lastDownloadTitle }}</h4>
              <span class="documents-download-modal__status-pill" :class="statusClassName(latestRequest.status)">
                {{ statusLabel(latestRequest.status) }}
              </span>
            </div>

            <p class="documents-download-modal__context-line">{{ latestRequest.scope_label || labels.fallbackScopeLabel }}</p>
            <p class="documents-download-modal__context-count">
              {{ labels.summaryLine(resolveDocumentTypeLabel(latestRequest.document_type), latestRequest.total_invoices || 0) }}
            </p>

            <p v-if="latestRequest.status === 'processing' || latestRequest.status === 'pending'" class="documents-download-modal__preparing-title">
              {{ labels.preparing.title }}
            </p>
            <p v-if="latestRequest.status === 'processing' || latestRequest.status === 'pending'" class="documents-download-modal__help">
              {{ labels.preparing.keepWorking }}
            </p>
            <p v-if="latestRequest.status === 'processing' || latestRequest.status === 'pending'" class="documents-download-modal__progress">
              {{ labels.preparing.packageProgress(latestRequest.completed_packages || 0, latestRequest.total_packages || 0) }}
            </p>

            <p v-if="latestRequest.status === 'expired'" class="documents-download-modal__expired">
              {{ labels.expiredMessage }}
            </p>

            <p v-if="latestRequest.expires_at && latestRequest.status !== 'expired'" class="documents-download-modal__help documents-download-modal__help-with-icon">
              <i class="fa fa-hourglass-half" aria-hidden="true"></i>
              <span>{{ labels.availableUntil(formatDateTime(latestRequest.expires_at)) }}</span>
            </p>

            <ul v-if="packageRows.length > 0" class="documents-download-modal__package-list">
              <li v-for="item in packageRows" :key="item.id" class="documents-download-modal__package-item">
                <span class="documents-download-modal__package-text">
                  {{ labels.packageLine(item.package_index, latestRequest.total_packages || 0) }}
                </span>
                <div class="documents-download-modal__package-right">
                  <span class="documents-download-modal__status-pill documents-download-modal__status-pill--small" :class="statusClassName(item.status)">
                    {{ statusLabel(item.status) }}
                  </span>
                  <div class="documents-download-modal__package-actions">
                    <button
                      v-if="item.can_download"
                      type="button"
                      class="documents-download-modal__package-icon-btn documents-download-modal__package-icon-btn--download"
                      :title="labels.actions.downloadPackage"
                      :aria-label="labels.actions.downloadPackage"
                      @click="emitDownloadPackage(item)"
                    >
                      <i class="fa fa-download" aria-hidden="true"></i>
                    </button>
                    <button
                      v-if="item.can_retry"
                      type="button"
                      class="documents-download-modal__package-icon-btn documents-download-modal__package-icon-btn--retry"
                      :title="labels.actions.retryPackage"
                      :aria-label="labels.actions.retryPackage"
                      @click="emitRetryPackage(item)"
                    >
                      <i class="fa fa-rotate-right" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </li>
            </ul>
          </section>

          <section v-if="totalInvoices > 0" class="app-modal__section documents-download-modal__info-section" role="note">
            <div class="documents-download-modal__info-card">
              <div class="documents-download-modal__info-row">
                <i class="fa fa-cubes" aria-hidden="true"></i>
                <p>{{ labels.packageLimitHint(packageLimit) }}</p>
              </div>
              <div class="documents-download-modal__info-row">
                <i class="fa fa-clock-o" aria-hidden="true"></i>
                <p>{{ labels.expiryHint(expiresHours) }}</p>
              </div>
            </div>
          </section>
        </div>

        <footer class="app-modal__footer documents-download-modal__footer">
          <div class="app-modal__footer-right">
            <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
              {{ labels.actions.close }}
            </button>
            <button
              v-if="showRefreshButton"
              type="button"
              class="btn btn-secondary"
              :disabled="submitting"
              @click="emitRefresh"
            >
              {{ labels.actions.refresh }}
            </button>
            <button
              v-if="showDownloadButton"
              type="button"
              class="btn btn-primary"
              :disabled="submitDisabled"
              @click="emitCreate"
            >
              {{ submitting ? labels.actions.downloading : labels.actions.download }}
            </button>
            <button
              v-if="latestRequest?.status === 'expired'"
              type="button"
              class="btn btn-primary"
              :disabled="submitting"
              @click="emitRegenerate"
            >
              {{ labels.actions.regenerate }}
            </button>
          </div>
        </footer>
      </div>
    </section>
  </div>
</template>

<script>
import { formatDateTime } from '@/utils/datePresentation';

export default {
  name: 'DocumentsDownloadModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      required: true,
    },
    context: {
      type: Object,
      default: null,
    },
    latestRequest: {
      type: Object,
      default: null,
    },
    packageLimit: {
      type: Number,
      default: 200,
    },
    expiresHours: {
      type: Number,
      default: 24,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    documentTypeOptions: {
      type: Array,
      default: () => ['pdf', 'xml', 'both'],
    },
    allowGroupBy: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'close',
    'create',
    'refresh',
    'download-package',
    'retry-package',
    'regenerate',
  ],
  data() {
    return {
      selectedDocumentType: 'both',
      selectedGroupBy: 'none',
    };
  },
  computed: {
    hasLatestRequest() {
      return !!(this.latestRequest && typeof this.latestRequest === 'object');
    },
    resolvedDocumentTypeOptions() {
      const known = ['pdf', 'xml', 'both'];
      const options = (Array.isArray(this.documentTypeOptions) ? this.documentTypeOptions : [])
        .map((option) => String(option || '').trim().toLowerCase())
        .filter((option) => known.includes(option));

      const uniqueOptions = [...new Set(options)];
      return uniqueOptions.length > 0 ? uniqueOptions : ['both'];
    },
    effectiveDocumentType() {
      if (this.resolvedDocumentTypeOptions.includes(this.selectedDocumentType)) {
        return this.selectedDocumentType;
      }

      return this.resolvedDocumentTypeOptions[0] || 'both';
    },
    totalInvoices() {
      return Number(this.context?.totalInvoices || 0) || 0;
    },
    resolvedScopeLabel() {
      return String(this.context?.scopeLabel || '').trim();
    },
    showDocumentTypeOptions() {
      return this.totalInvoices > 0 && this.resolvedDocumentTypeOptions.length > 1;
    },
    showGroupingOptions() {
      return this.totalInvoices > 0 && this.allowGroupBy;
    },
    showRefreshButton() {
      const status = String(this.latestRequest?.status || '').trim().toLowerCase();
      if (status === 'completed') {
        return false;
      }

      return this.hasLatestRequest;
    },
    showDownloadButton() {
      return this.totalInvoices > 0;
    },
    submitDisabled() {
      return this.submitting || this.totalInvoices <= 0;
    },
    packageRows() {
      return Array.isArray(this.latestRequest?.packages) ? this.latestRequest.packages : [];
    },
  },
  watch: {
    show(nextValue) {
      if (!nextValue) {
        this.selectedDocumentType = this.resolvedDocumentTypeOptions[0] || 'both';
        this.selectedGroupBy = 'none';
      }
    },
    documentTypeOptions: {
      deep: true,
      handler() {
        if (!this.resolvedDocumentTypeOptions.includes(this.selectedDocumentType)) {
          this.selectedDocumentType = this.resolvedDocumentTypeOptions[0] || 'both';
        }
      },
    },
    latestRequest: {
      immediate: true,
      handler(nextValue) {
        const incoming = String(nextValue?.group_by || '').trim().toLowerCase();
        if (this.allowGroupBy && ['none', 'customer', 'month'].includes(incoming)) {
          this.selectedGroupBy = incoming;
        }

        const documentType = String(nextValue?.document_type || '').trim().toLowerCase();
        if (this.resolvedDocumentTypeOptions.includes(documentType)) {
          this.selectedDocumentType = documentType;
        }
      },
    },
  },
  methods: {
    formatDateTime,
    emitClose() {
      this.$emit('close');
    },
    emitRefresh() {
      this.$emit('refresh');
    },
    emitCreate() {
      this.$emit('create', {
        documentType: this.effectiveDocumentType,
        groupBy: this.allowGroupBy ? this.selectedGroupBy : 'none',
      });
    },
    emitDownloadPackage(item) {
      this.$emit('download-package', item);
    },
    emitRetryPackage(item) {
      this.$emit('retry-package', item);
    },
    emitRegenerate() {
      this.$emit('regenerate', {
        documentType: this.effectiveDocumentType,
        groupBy: this.allowGroupBy ? this.selectedGroupBy : 'none',
      });
    },
    setGroupBy(value) {
      const resolved = String(value || '').trim().toLowerCase();
      if (!['none', 'customer', 'month'].includes(resolved)) {
        this.selectedGroupBy = 'none';
        return;
      }

      this.selectedGroupBy = resolved;
    },
    resolveDocumentTypeLabel(documentType) {
      const key = String(documentType || 'both').trim().toLowerCase();
      if (key === 'pdf') {
        return this.labels.documentTypes.pdf;
      }

      if (key === 'xml') {
        return this.labels.documentTypes.xml;
      }

      return this.labels.documentTypes.both;
    },
    statusLabel(status) {
      const key = String(status || '').trim().toLowerCase();
      if (key === 'pending') {
        return this.labels.status.pending;
      }

      if (key === 'processing') {
        return this.labels.status.processing;
      }

      if (key === 'completed') {
        return this.labels.status.completed;
      }

      if (key === 'partial_failed') {
        return this.labels.status.partialFailed;
      }

      if (key === 'failed') {
        return this.labels.status.failed;
      }

      if (key === 'expired') {
        return this.labels.status.expired;
      }

      return this.labels.status.pending;
    },
    statusClassName(status) {
      const key = String(status || '').trim().toLowerCase();
      if (key === 'pending') {
        return 'is-pending';
      }

      if (key === 'completed') {
        return 'is-success';
      }

      if (key === 'failed' || key === 'expired') {
        return 'is-danger';
      }

      if (key === 'partial_failed') {
        return 'is-warning';
      }

      return 'is-neutral';
    },
  },
};
</script>

<style scoped>
.documents-download-modal__dialog {
  width: min(860px, calc(100vw - 2rem));
}

.documents-download-modal__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.documents-download-modal__body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  flex: 1;
}

.documents-download-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.documents-download-modal__context-line,
.documents-download-modal__context-count,
.documents-download-modal__help,
.documents-download-modal__progress,
.documents-download-modal__expired,
.documents-download-modal__preparing-title,
.documents-download-modal__empty-message {
  margin: 0 0 0.75rem;
}

.documents-download-modal__context-count {
  font-weight: 600;
}

.documents-download-modal__types {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin: 1rem 0;
}

.documents-download-modal__type-item.is-active {
  border-color: rgba(var(--theme-default-rgb, 36, 105, 239), 0.45);
}

.documents-download-modal__info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  border-radius: 8px;
  border: 1px solid #ead8ab;
  background: #fff8e8;
  color: #6c571a;
  padding: 0.65rem 0.75rem;
  margin: 0 0 0.75rem;
}

.documents-download-modal__info-box i {
  margin-top: 0.15rem;
}

.documents-download-modal__info-box p {
  margin: 0;
  line-height: 1.35;
}

.documents-download-modal__info-card {
  border: 1px solid #d9e3ef;
  background: #f8fbff;
  border-radius: 8px;
  padding: 0.75rem;
  display: grid;
  gap: 0.55rem;
}

.documents-download-modal__info-row {
  display: flex;
  align-items: baseline;
  gap: 0.55rem;
  color: #334155;
}

.documents-download-modal__info-row i {
  position: relative;
  top: 1px;
}

.documents-download-modal__info-row p {
  margin: 0;
  line-height: 1.35;
}

.documents-download-modal__help-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.documents-download-modal__status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.documents-download-modal__status-pill.is-success {
  background: rgba(9, 121, 52, 0.14);
  color: #0b6b2f;
}

.documents-download-modal__status-pill.is-danger {
  background: rgba(170, 19, 36, 0.12);
  color: #941425;
}

.documents-download-modal__status-pill.is-warning {
  background: rgba(180, 96, 11, 0.14);
  color: #8a4f0c;
}

.documents-download-modal__status-pill.is-neutral {
  background: rgba(30, 41, 59, 0.1);
  color: #334155;
}

.documents-download-modal__status-pill.is-pending {
  background: #e5e7eb;
  color: #374151;
}

.documents-download-modal__status-pill--small {
  font-size: 0.7rem;
  padding: 0.15rem 0.55rem;
}

.documents-download-modal__package-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.6rem;
}

.documents-download-modal__package-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  padding: 0.6rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.documents-download-modal__package-text {
  font-weight: 500;
  color: #334155;
}

.documents-download-modal__package-actions {
  display: inline-flex;
  gap: 0.5rem;
}

.documents-download-modal__package-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  color: #475569;
  padding: 0;
}

.documents-download-modal__package-icon-btn--download {
  border-color: rgba(15, 118, 110, 0.35);
  background: rgba(13, 148, 136, 0.12);
  color: #0f766e;
}

.documents-download-modal__package-icon-btn--download:hover,
.documents-download-modal__package-icon-btn--download:focus {
  background: rgba(13, 148, 136, 0.2);
  color: #0f766e;
}

.documents-download-modal__package-icon-btn--retry {
  border-color: rgba(180, 83, 9, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #92400e;
}

.documents-download-modal__package-icon-btn--retry:hover,
.documents-download-modal__package-icon-btn--retry:focus {
  background: rgba(245, 158, 11, 0.2);
  color: #78350f;
}

.documents-download-modal__package-right {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.documents-download-modal__info-section {
  margin-top: auto;
  padding-top: 8px;
}

.documents-download-modal__grouping-title {
  margin: 0 0 0.55rem;
  font-weight: 600;
  color: #334155;
}

.documents-download-modal__grouping-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

.documents-download-modal__grouping-option.is-active {
  border-color: rgba(var(--theme-default-rgb, 36, 105, 239), 0.45);
}

.documents-download-modal__footer {
  border-top: 1px solid rgba(226, 232, 240, 0.85);
}

.documents-download-modal__last-download {
  margin-top: 1rem;
}

@media (max-width: 767px) {
  .documents-download-modal__types {
    grid-template-columns: 1fr;
  }

  .documents-download-modal__grouping-options {
    grid-template-columns: 1fr;
  }

  .documents-download-modal__package-item {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
